import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Gem, Hammer, Sparkles } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { whatsappLink } from "@/lib/site";
import ProductCard from "@/components/ProductCard";
import Marquee from "@/components/Marquee";
import VideoCarousel from "@/components/VideoCarousel";
import CustomAtelier from "@/components/CustomAtelier";
import Reveal, { EASE } from "@/components/Reveal";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6b97e868-50f8-49a5-b8db-59f3ebd74a72/images/381362850a5c684a24b2a2c23ede9ce801640ff193006da75a91f825a8eea220.jpeg";
const HERITAGE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/6b97e868-50f8-49a5-b8db-59f3ebd74a72/images/3a3b54ac2af3503d0da482cd867c5cadd793ddf38a627f294e384b96fbdc370e.jpeg";

const TILES = [
  { label: "Necklaces", cat: "Necklaces", img: "/images/necklaces/antique-lakshmi-kundan-stone-necklace-with-earrings_1.jpg" },
  { label: "Harams", cat: "Harams", img: "/images/harams/antique-mango-lakshmi-haram-with-earrings_1.jpg" },
  { label: "Chokers", cat: "Chokers", img: "/images/chokers/antique-green-kundan-stone-peacock-choker-with-earrings_1.jpg" },
  { label: "Chains", cat: "Chains", img: "/images/chains/antique-lakshmi-pendant-chain-with-earring_1.jpg" },
];

function MaskedLine({ children, delay, className }) {
  return (
    <span className="mask-reveal">
      <motion.span
        className={`block ${className || ""}`}
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Hero() {
  const { openEnquiry } = useShop();
  const frameRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 110]);
  const glowY = useTransform(scrollY, [0, 700], [0, -60]);

  const onMouseMove = (e) => {
    const r = frameRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -y * 7, ry: x * 9 });
  };

  return (
    <section
      className="relative min-h-[100svh] flex items-center overflow-hidden"
      onMouseMove={onMouseMove}
      onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
      data-testid="hero-section"
    >
      <motion.div aria-hidden style={{ y: glowY }} className="absolute -top-32 right-[-10%] h-[540px] w-[540px] rounded-full bg-gold2/20 blur-[130px]" />
      <motion.div aria-hidden className="absolute bottom-[-20%] left-[-8%] h-[420px] w-[420px] rounded-full bg-royal/10 blur-[120px]" />

      <div className="relative z-10 mx-auto grid w-full max-w-[1440px] gap-14 px-4 pt-28 pb-16 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-16 lg:pt-20 lg:pb-24">
        <div>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }} className="overline-tag font-semibold">
            South Indian Heritage Jewellery
          </motion.p>
          <h1 className="mt-6 font-display font-semibold text-ink leading-[0.98] text-[11vw] sm:text-6xl lg:text-[86px]">
            <MaskedLine delay={0.25}><span className="text-gold-gradient italic">Tradition.</span></MaskedLine>
            <MaskedLine delay={0.42}>Timeless Elegance.</MaskedLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75, ease: EASE }} className="mt-7 max-w-md text-[15px] font-semibold leading-relaxed text-ink2">
            Celebrate every occasion with antique-finish temple jewellery, elegant harams, necklaces and statement pieces — royal South Indian style, at prices you&apos;ll love.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/shop" className="btn-gold" data-testid="hero-explore-cta">
              Explore Our Collection <ArrowUpRight size={15} />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.15 }} className="mt-12 flex flex-wrap gap-2.5">
            <span className="chip">Antique Finish</span>
            <span className="chip">Skin-Safe Plating</span>
            <span className="chip">Handcrafted</span>
            <span className="chip">One of One</span>
          </motion.div>
        </div>

        <div className="relative" style={{ perspective: 1000 }}>
          <motion.div
            ref={frameRef}
            style={{ y: imgY, rotateX: tilt.rx, rotateY: tilt.ry, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.4, ease: EASE }}
            className="relative mx-auto w-[86%] max-w-[440px] lg:w-full"
            data-testid="hero-visual"
          >
            <div aria-hidden className="absolute -inset-3 arch-frame border border-gold/45 translate-x-4 translate-y-4" />
            <div className="arch-frame overflow-hidden border border-gold/30 shadow-[0_50px_100px_-40px_rgba(28,23,18,0.45)]">
              <img src={HERO_IMG} alt="Model in Sashti antique jewellery" className="aspect-[3/4.1] w-full object-cover" />
            </div>
            <div className="float-slow absolute -left-6 sm:-left-12 bottom-14 z-10 w-44 sm:w-52 card-surface p-4 shadow-xl" style={{ transform: "translateZ(60px)" }}>
              <p className="overline-tag !text-[9px]">Atelier Pick</p>
              <p className="mt-1.5 font-display text-lg font-semibold leading-tight">Peacock Jhumkas</p>
              <p className="mt-1 font-mono text-xs text-ink2">Antique Finish</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CategoryTile({ t, images }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length < 2) return;
    const iv = setInterval(() => setIdx((x) => (x + 1) % images.length), 3400);
    return () => clearInterval(iv);
  }, [images.length]);

  const src = images[idx] || t.img;

  return (
    <Link
      to={`/shop?category=${encodeURIComponent(t.cat)}`}
      className="group relative block overflow-hidden bg-bg2 border border-line"
      data-testid={`collection-tile-${t.cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="relative aspect-[3/4] w-full bg-bg2">
        <AnimatePresence>
          <motion.img
            key={`${t.cat}-${idx}`}
            src={src}
            alt={`${t.label} — Sashti Jewels antique finish jewellery`}
            loading="lazy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeInOut" }}
            className="absolute inset-0 h-full w-full object-contain p-2"
          />
        </AnimatePresence>
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#14100A]/85 to-transparent p-4 pt-10">
        <p className="font-display text-xl font-semibold text-[#F7F4EE]">{t.label}</p>
        <p className="mt-0.5 flex items-center gap-1.5 font-cinzel text-[10px] uppercase text-gold2" style={{ letterSpacing: "0.24em" }}>
          Explore <ArrowUpRight size={12} />
        </p>
      </div>
    </Link>
  );
}

function Collections() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((d) => setProducts(d.data)).catch(() => {});
  }, []);

  const catImages = (cat) => {
    const imgs = products.filter((p) => p.category === cat).flatMap((p) => p.images || []);
    return imgs.length ? imgs : [];
  };

  return (
    <section className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-20 lg:py-28" data-testid="collections-section">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="overline-tag">The Collections</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-ink leading-tight">
              Chosen by occasion,<br />worn by generations
            </h2>
          </div>
          <Link to="/shop" className="group flex items-center gap-2 font-cinzel text-[11px] font-semibold uppercase text-gold" style={{ letterSpacing: "0.22em" }}>
            View all jewels <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </Reveal>

      <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-3">
        {TILES.map((t, i) => (
          <Reveal key={t.cat} delay={(i % 3) * 0.07}>
            <CategoryTile t={t} images={catImages(t.cat)} />
          </Reveal>
        ))}
        <Under500Tile />
        <CustomAtelierTile />
      </div>
    </section>
  );
}

function Under500Tile() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    api.get("/products").then((d) => setItems(d.data.filter((p) => p.price < 500))).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const iv = setInterval(() => {
      if (!paused.current) setIdx((i) => (i + 1) % items.length);
    }, 2600);
    return () => clearInterval(iv);
  }, [items]);

  const cur = items[idx];

  return (
    <Reveal delay={0.21}>
      <Link
        to="/shop?max=500"
        className="group relative block overflow-hidden border border-gold/40 bg-bg2"
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => (paused.current = false)}
        data-testid="collection-tile-under-500"
      >
        <div className="relative aspect-[3/4] w-full bg-bg2">
          <AnimatePresence mode="wait">
            {cur && (
              <motion.img
                key={cur._id}
                src={cur.images[0]}
                alt={cur.name}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 h-full w-full object-contain p-4"
              />
            )}
          </AnimatePresence>
          {cur && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#14100A]/75 to-transparent p-4 pt-10">
              <p className="truncate font-display text-base font-semibold text-[#F7F4EE]">{cur.name}</p>
              <p className="font-mono text-[11px] text-gold2">{inr(cur.price)} · Shop Now</p>
            </div>
          )}
        </div>
        <span className="absolute left-3 top-3 bg-gold px-4 py-2 font-cinzel text-xs sm:text-sm font-bold uppercase text-[#241A08] shadow-lg" style={{ letterSpacing: "0.14em" }}>
          Under ₹500
        </span>
      </Link>
    </Reveal>
  );
}

function CustomAtelierTile() {
  const [pics, setPics] = useState([]);
  useEffect(() => {
    api.get("/products").then((d) => {
      const p = d.data;
      setPics(p.length > 2 ? [p[0], p[Math.floor(p.length / 2)], p[p.length - 1]] : p);
    }).catch(() => {});
  }, []);

  return (
    <Reveal delay={0.28}>
      <button
        onClick={() => document.getElementById("atelier")?.scrollIntoView({ behavior: "smooth" })}
        className="group relative block h-full w-full overflow-hidden border border-gold/40 bg-bg2 text-left"
        data-testid="collection-tile-atelier"
      >
        <div className="relative aspect-[3/4] w-full p-5">
          <Sparkles size={20} className="text-gold" />
          <div className="absolute inset-x-0 top-[15%] flex justify-center px-4" data-testid="atelier-tile-collage">
            {pics.map((p, i) => (
              <img
                key={p._id}
                src={p.images[0]}
                alt=""
                loading="lazy"
                className={`h-28 w-16 sm:h-36 sm:w-24 border border-gold/40 bg-surface object-contain p-1.5 shadow-lg ${i ? "-ml-5 sm:-ml-8" : ""}`}
                style={{ transform: `rotate(${(i - 1) * 7}deg)`, zIndex: i === 1 ? 2 : 1 }}
              />
            ))}
          </div>
          <div className="absolute inset-x-0 bottom-0 p-5">
            <p className="font-display text-2xl font-semibold text-ink">Custom Atelier</p>
            <p className="mt-1 text-sm text-ink2">Design your own piece with our master artisans</p>
            <p className="mt-2 flex items-center gap-1.5 font-cinzel text-[10px] uppercase text-gold" style={{ letterSpacing: "0.24em" }}>
              Enter the Atelier <ArrowUpRight size={12} />
            </p>
          </div>
        </div>
        <ArrowUpRight size={16} className="absolute right-5 top-5 text-gold transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
      </button>
    </Reveal>
  );
}

function Spotlight({ products }) {
  return (
    <section className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-20 lg:py-28" data-testid="spotlight-section">
      <Reveal>
        <div className="text-center">
          <p className="overline-tag">The Spotlight Edit</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-ink">Pieces the atelier is proud of</h2>
        </div>
      </Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {products.map((p, i) => (
          <ProductCard key={p._id} product={p} index={i} />
        ))}
      </div>
    </section>
  );
}

function Heritage() {
  return (
    <section id="heritage" className="relative overflow-hidden bg-deep text-[#F7F4EE]" data-testid="heritage-section">
      <div aria-hidden className="absolute -top-40 left-1/3 h-[480px] w-[480px] rounded-full bg-gold2/10 blur-[130px]" />
      <div className="relative mx-auto grid max-w-[1440px] gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:items-center lg:px-16 py-20 lg:py-32">
        <Reveal>
          <div className="relative mx-auto w-[84%] max-w-[420px] lg:w-full">
            <div aria-hidden className="absolute -inset-3 arch-frame border border-gold2/40 -translate-x-4 translate-y-4" />
            <div className="arch-frame overflow-hidden border border-gold2/30">
              <img src={HERITAGE_IMG} alt="Sashti heritage craft" loading="lazy" className="aspect-[3/4] w-full object-cover" />
            </div>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <p className="overline-tag">The Sashti Legacy</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]">
              Antique soul,<br /><span className="italic text-gold2">honest price.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-7 max-w-lg text-[15px] leading-relaxed text-[#C8BFB0]">
              Named for Murugan&apos;s vel — the spear that cuts through illusion — Sashti Jewels
              carries the temple traditions of the South: repoussé deities, kemp-stone harams and
              jhumkas that announce a bride before she enters. Every piece is antique gold plating
              over skin-safe brass, finished by hand, and one of one — the heirloom look, worn
              every day.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 space-y-6">
              {[
                { icon: Hammer, t: "Master Artisans", d: "Generations of sthapathis who still finish every piece by hand." },
                { icon: Gem, t: "Honest Craft", d: "Antique gold plating, kemp and kundan stones set the old way." },
                { icon: Sparkles, t: "One of One", d: "Slight variations are not flaws — they are the signature of the hand." },
              ].map((v, i) => (
                <div key={i} className="flex gap-5">
                  <span className="mt-1 grid h-11 w-11 shrink-0 place-items-center border border-gold2/40 text-gold2">
                    <v.icon size={17} />
                  </span>
                  <div>
                    <p className="font-cinzel text-[12px] uppercase text-gold2" style={{ letterSpacing: "0.2em" }}>{v.t}</p>
                    <p className="mt-1.5 text-sm text-[#C8BFB0] leading-relaxed">{v.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ConciergeBand() {
  const { openEnquiry } = useShop();
  return (
    <section className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
      <Reveal>
        <div className="relative overflow-hidden border border-gold/30 bg-bg2 px-6 py-16 text-center sm:px-16">
          <div aria-hidden className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-gold2/15 blur-[90px]" />
          <p className="overline-tag">Private Concierge</p>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl sm:text-5xl font-semibold text-ink leading-tight">
            Begin your heirloom with a conversation
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-ink2 leading-relaxed">
            Tell us the occasion — a muhurtham, a first gift, a milestone — and our
            concierge will curate pieces or commission something made only for you.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <button onClick={() => openEnquiry()} className="btn-gold" data-testid="concierge-open-form">Book Private Concierge</button>
            <a
              href={whatsappLink("Hello Sashti Jewels, I'd like to book a private concierge appointment.")}
              target="_blank" rel="noopener noreferrer" className="btn-ghost-gold" data-testid="concierge-whatsapp"
            >
              WhatsApp the Atelier
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((d) => setFeatured(d.data.slice(0, 10)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <main>
      <Hero />
      <Marquee />
      <Collections />
      <Spotlight products={featured} />
      <VideoCarousel />
      <CustomAtelier />
      <Heritage />
      <ConciergeBand />
    </main>
  );
}
